/**
 * @file <bk-wilaya-checker>: "do you deliver to my area?" (sections/feature-cards.liquid).
 *
 * The merchant owns every word: the result sentence is a template from the section settings
 * ({wilaya} and {delai}); the chip label and the default delay arrive as data-* attributes rendered
 * with `| t`. Nothing is computed here: no fee, no promise the store has not made. When the area has
 * no delay and the template needs one, only the chip is shown, never a sentence with a hole in it.
 *
 * The chosen area is written to localStorage through setWilaya(), so the WhatsApp links, the product
 * page and the review form can name the same area. Every checker on the page follows the
 * `bk:wilaya:change` event. Storage can be blocked, so the helpers never throw.
 */
import { getWilaya, setWilaya, subscribe } from '@theme/utils';

class BkWilayaChecker extends HTMLElement {
  /** @type {HTMLSelectElement | null} */
  #select = null;

  /** @type {HTMLElement | null} */
  #chip = null;

  /** @type {HTMLElement | null} */
  #chipText = null;

  /** @type {HTMLElement | null} */
  #text = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    const select = this.querySelector('[data-wilaya-select]');
    if (!(select instanceof HTMLSelectElement)) return;

    this.#select = select;
    this.#chip = this.querySelector('[data-wilaya-chip]');
    this.#chipText = this.querySelector('[data-wilaya-chip-text]');
    this.#text = this.querySelector('[data-wilaya-text]');

    this.#controller = new AbortController();
    select.addEventListener('change', () => this.#onChange(), { signal: this.#controller.signal });
    // subscribe() hands the payload first: { wilaya }.
    this.#unsubscribe = subscribe('bk:wilaya:change', (detail) => this.#onExternalChange(detail?.wilaya ?? ''));

    const saved = getWilaya();
    if (saved) this.#apply(saved);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  #onChange() {
    const value = this.#select?.value ?? '';
    this.#render(value);
    setWilaya(value);
  }

  /** @param {string} wilaya */
  #onExternalChange(wilaya) {
    if (!this.#select || this.#select.value === wilaya) return;
    this.#apply(wilaya);
  }

  /** @param {string} value */
  #apply(value) {
    const select = this.#select;
    if (!select) return;
    const match = [...select.options].some((option) => option.value === value);
    select.value = match ? value : '';
    this.#render(select.value);
  }

  /** @param {string} value */
  #render(value) {
    const chip = this.#chip;
    const text = this.#text;

    if (!value) {
      if (chip) chip.hidden = true;
      if (text) text.textContent = '';
      return;
    }

    const option = this.#select?.selectedOptions?.[0];
    const areaLabel = option?.textContent?.trim() || value;
    const label = this.dataset.availableLabel ?? '';
    if (chip && this.#chipText) {
      this.#chipText.textContent = label ? `${label} · ${areaLabel}` : areaLabel;
      chip.hidden = false;
    }

    if (!text) return;
    const delay = (option?.dataset.delay || this.dataset.defaultDelay || '').trim();
    const template = this.dataset.resultTemplate ?? '';
    if (!template || (template.includes('{delai}') && !delay)) {
      text.textContent = '';
      return;
    }
    text.textContent = template.replaceAll('{wilaya}', value).replaceAll('{delai}', delay).trim();
  }
}

if (!customElements.get('bk-wilaya-checker')) customElements.define('bk-wilaya-checker', BkWilayaChecker);
